import { useEffect, useState } from "react";
import { ShieldCheck, Vault, Users, DeviceMobile } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login, register, verify2FA } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  // Public config — drives whether new sign-ups are allowed
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  // 2FA state
  const [totpPending, setTotpPending] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  useEffect(() => {
    api.get("/config")
      .then((r) => setRegistrationEnabled(r.data?.registration_enabled !== false))
      .catch(() => setRegistrationEnabled(true));
  }, []);

  // If signups are disabled and we're sitting on the register tab, force back to login.
  useEffect(() => {
    if (!registrationEnabled && mode === "register") setMode("login");
  }, [registrationEnabled, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        const r = await api.post("/auth/forgot-password", { email: form.email });
        toast.success(r.data?.message || "If an account with that email exists, a reset link has been sent.");
        setMode("login");
      } else if (totpPending) {
        await verify2FA(totpPending, totpCode);
        window.location.href = "/networth";
      } else if (mode === "login") {
        const result = await login(form.email, form.password);
        if (result.requires_2fa) {
          setTotpPending(result.totp_token);
        } else {
          window.location.href = "/networth";
        }
      } else {
        await register(form.email, form.password, form.name);
        window.location.href = "/networth";
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Authentication failed");
    } finally { setBusy(false); }
  };

  // ── 2FA code entry screen ─────────────────────────────────────────────────
  if (totpPending) {
    return (
      <div className="min-h-screen grid lg:grid-cols-2 bg-background">
        <div className="relative hidden lg:flex flex-col justify-between p-12 bg-foreground text-background overflow-hidden">
          <div className="flex items-center gap-2">
            <Vault size={28} weight="fill" />
            <span className="font-display font-black text-xl tracking-tight">CONTINUITY</span>
          </div>
          <div className="space-y-6">
            <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tighter">
              Two-factor<br />authentication.
            </h1>
            <p className="text-base text-background/70 max-w-md leading-relaxed">
              Open your authenticator app and enter the 6-digit code for OurHomebase.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 text-xs uppercase tracking-[0.2em] text-background/50">
            <div><ShieldCheck size={20} className="mb-2" /> 2FA active</div>
            <div><DeviceMobile size={20} className="mb-2" /> Auth app</div>
            <div><Vault size={20} className="mb-2" /> Secured</div>
          </div>
        </div>

        <div className="flex items-center justify-center p-8">
          <div className="w-full max-w-sm space-y-7 animate-fade-up">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">Step 2 of 2</p>
              <h2 className="font-display text-3xl font-black tracking-tight">
                {useRecovery ? "Recovery code" : "Enter your code"}
              </h2>
              <p className="text-sm text-muted-foreground mt-2">
                {useRecovery
                  ? "Enter one of your 8-character recovery codes."
                  : "Open your authenticator app and enter the 6-digit code."}
              </p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">
                  {useRecovery ? "Recovery code" : "6-digit code"}
                </Label>
                <Input
                  data-testid="totp-code-input"
                  autoFocus
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\s/g, ""))}
                  placeholder={useRecovery ? "xxxxxxxx" : "000000"}
                  maxLength={useRecovery ? 11 : 6}
                  className="rounded-sm mt-1.5 font-mono text-center text-2xl tracking-[0.4em]"
                  inputMode="numeric"
                />
              </div>
              <Button data-testid="totp-verify-button" type="submit" disabled={busy || !totpCode} className="w-full h-11 font-semibold rounded-sm">
                {busy ? "Verifying…" : "Verify"}
              </Button>
            </form>
            <div className="text-center space-y-2">
              <button
                className="text-sm text-muted-foreground hover:text-foreground hover:underline"
                onClick={() => { setUseRecovery(!useRecovery); setTotpCode(""); }}
              >
                {useRecovery ? "Use authenticator app instead" : "Use a recovery code instead"}
              </button>
              <div />
              <button
                className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                onClick={() => { setTotpPending(null); setTotpCode(""); setUseRecovery(false); }}
              >
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Normal login / register screen ────────────────────────────────────────
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-foreground text-background overflow-hidden">
        <div className="flex items-center gap-2">
          <Vault size={28} weight="fill" />
          <span className="font-display font-black text-xl tracking-tight">CONTINUITY</span>
        </div>
        <div className="space-y-6">
          <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tighter">
            Your family office,<br />built to outlast you.
          </h1>
          <p className="text-base text-background/70 max-w-md leading-relaxed">
            One trustworthy system of record for real estate, mineral royalties, private equity, and
            every entity — so a spouse, heir, or advisor can step in the moment they need to.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-4 text-xs uppercase tracking-[0.2em] text-background/50">
          <div><ShieldCheck size={20} className="mb-2" /> Audit trail</div>
          <div><Users size={20} className="mb-2" /> Multi-operator</div>
          <div><Vault size={20} className="mb-2" /> Continuity Pack</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-7 animate-fade-up">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">
              {mode === "login" ? "Welcome back" : mode === "forgot" ? "Account recovery" : "Get started"}
            </p>
            <h2 className="font-display text-3xl font-black tracking-tight">
              {mode === "login" ? "Sign in" : mode === "forgot" ? "Reset password" : "Create your account"}
            </h2>
            {mode === "forgot" && (
              <p className="text-sm text-muted-foreground mt-2">
                Enter your email and we'll send you a link to reset your password.
              </p>
            )}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Name</Label>
                <Input data-testid="auth-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1.5" placeholder="Your name" />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Email</Label>
              <Input data-testid="auth-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm mt-1.5" placeholder="you@example.com" />
            </div>
            {mode !== "forgot" && (
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider font-bold">Password</Label>
                  {mode === "login" && (
                    <button type="button" data-testid="forgot-password-link"
                      onClick={() => setMode("forgot")}
                      className="text-xs text-muted-foreground hover:text-foreground hover:underline">
                      Forgot password?
                    </button>
                  )}
                </div>
                <Input data-testid="auth-password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm mt-1.5" placeholder={mode === "register" ? "At least 8 characters" : "••••••••"} />
              </div>
            )}
            <Button data-testid="auth-submit-button" type="submit" disabled={busy} className="w-full h-11 font-semibold rounded-sm">
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : mode === "forgot" ? "Send reset link" : "Create account"}
            </Button>
          </form>

          {mode === "forgot" ? (
            <p className="text-sm text-muted-foreground text-center">
              <button data-testid="back-to-signin" onClick={() => setMode("login")} className="font-semibold text-foreground hover:underline">
                Back to sign in
              </button>
            </p>
          ) : (
          <p className="text-sm text-muted-foreground text-center">
            {registrationEnabled ? (
              <>
                {mode === "login" ? "New here?" : "Already have an account?"}{" "}
                <button data-testid="auth-toggle-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="font-semibold text-foreground hover:underline">
                  {mode === "login" ? "Create an account" : "Sign in"}
                </button>
              </>
            ) : (
              <span data-testid="signups-disabled-note">
                New sign-ups are currently disabled. Contact your administrator if you need access.
              </span>
            )}
          </p>
          )}
        </div>
      </div>
    </div>
  );
}
